import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Eyebrow, Lines, EASE } from "./Motion";
import { IMG, img } from "@/lib/site";

const SERVICES = [
  { id: "decks", n: "01", title: "New Construction Decks", img: IMG.deckGlass, src: "/projects/new-construction-deck.jpg", pos: "50% 50%", span: "md:col-span-5 md:row-span-2", items: ["Composite & hardwood", "Multi-level & elevated", "Cable, aluminum & wood rail"], lead: true },
  { id: "porches", n: "02", title: "Covered Porches", img: IMG.deckCovered, src: "/projects/covered-porch.jpg", pos: "40% 55%", span: "md:col-span-7 md:row-span-2", items: ["Front & back porches", "Roofs, ceilings & lighting"] },
  { id: "siding", n: "03", title: "Siding", img: IMG.boardBatten, src: "/projects/siding.jpg", pos: "50% 55%", span: "md:col-span-7 md:row-span-2", items: ["Board-and-batten, lap, shake", "Wrap & flashing"] },
  { id: "exterior", n: "04", title: "Exterior Upgrades", img: IMG.siding, src: "/projects/exterior-upgrade.jpg", pos: "50% 50%", span: "md:col-span-5 md:row-span-2", items: ["Windows & doors", "Trim, soffit & fascia", "Steps & railings"] },
];

const Card = ({ s, i }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-4%", "4%"]);
  return (
  <motion.a
    ref={ref}
    href="#quote"
    data-testid={`service-card-${s.id}`}
    data-cursor="Quote"
    initial={{ opacity: 0, clipPath: "inset(12% 6% 12% 6%)" }}
    whileInView={{ opacity: 1, clipPath: "inset(0% 0% 0% 0%)" }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1.2, delay: (i % 2) * 0.12, ease: EASE }}
    className={`group relative isolate flex min-h-[300px] flex-col justify-between overflow-hidden bg-[#0A0A0A] p-6 md:p-8 ${s.span}`}
  >
    <motion.img style={{ y, scale: 1.08, objectPosition: s.pos || "50% 50%" }} src={s.src || img(s.img, s.lead ? 1600 : 1400)} alt={s.title} loading="lazy"
      className="absolute inset-0 -z-10 h-full w-full object-cover transition-[filter] duration-700 ease-out group-hover:brightness-110" />
    <div className="absolute inset-x-0 bottom-0 -z-10 h-[55%] bg-gradient-to-t from-[#0A0A0A]/90 via-[#0A0A0A]/40 to-transparent" />
    <div className="absolute inset-x-0 top-0 -z-10 h-24 bg-gradient-to-b from-[#0A0A0A]/40 to-transparent" />
    <div className="flex items-start justify-between">
      <span className="font-mono text-xs tracking-[0.2em] text-white/70">{s.n}</span>
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-white/25 text-white backdrop-blur-md transition-[background-color,color,transform] duration-500 group-hover:rotate-45 group-hover:bg-white group-hover:text-[#0A0A0A]">
        <ArrowUpRight className="h-4 w-4" />
      </span>
    </div>
    <div>
      <h3 className={`font-display font-bold tracking-tight text-white ${s.lead ? "text-4xl md:text-5xl" : "text-2xl md:text-4xl"}`}>{s.title}</h3>
      <ul className="mt-4 flex flex-wrap gap-2">
        {s.items.map((it) => (
          <li key={it} className="rounded-full border border-white/20 bg-[#0A0A0A]/40 px-3 py-1 font-mono text-[0.65rem] uppercase tracking-[0.15em] text-neutral-200 backdrop-blur-sm">{it}</li>
        ))}
      </ul>
    </div>
  </motion.a>
  );
};

export const Services = () => (
  <section id="services" data-testid="section-services" className="relative bg-[#F2F1ED] px-5 py-28 text-[#0A0A0A] md:px-10 md:py-40">
    <div className="mx-auto max-w-[1500px]">
      <div className="mb-16 grid gap-10 md:mb-24 md:grid-cols-12">
        <div className="md:col-span-7">
          <Eyebrow className="!text-[#0A0A0A]/60">What we build</Eyebrow>
          <h2 className="mt-6 font-display text-4xl font-bold leading-[1] tracking-tighter md:text-6xl">
            <Lines lines={["Four things. Done right.", <span key="a" className="font-serif font-normal italic text-[#0A0A0A]/55">The spaces you live outside.</span>]} />
          </h2>
        </div>
        <p className="self-end text-base leading-relaxed text-[#0A0A0A]/65 md:col-span-4 md:col-start-9 md:text-lg">
          The work that changes how a home feels every day: the deck you host on, the porch you greet guests on, the siding that makes the whole street look twice.
        </p>
      </div>
      <div className="grid auto-rows-[360px] grid-cols-1 gap-3 md:auto-rows-[300px] md:grid-cols-12">
        {SERVICES.map((s, i) => <Card key={s.id} s={s} i={i} />)}
      </div>
    </div>
  </section>
);
